import { reactive } from "vue";

export function panelizationPropositionCalculation({
  debouncedTruckLength,
  debouncedTruckWidth,
  debouncedTruckHeight,
  thickness,
}) {

  function createWallTypes(selectedWallType, maxPanelLength) {
    const panelLength = Number(maxPanelLength?.value ?? maxPanelLength);

    return reactive((selectedWallType?.value ?? [])
      .filter(item => item.parameters?.some(p => p.area != null && p.area > 0))
      .map(item => {
        const panels = (item.parameters ?? [])
          .map((param, index) => {
            if (param.curveLength != null) {
              return {
                id: item.ids?.[index],
                ratio: panelLength > 0 ? (param.curveLength / panelLength) : 0,
                selectedOption: 1,
              };
            }
            return undefined;
          })
          .filter(panel => panel !== undefined);

        const totalCurveLength = (item.parameters ?? []).reduce(
          (sum, p) => sum + (p.curveLength ?? 0), 0
        );
        
        const wallTypes = {
          type: item.type,
          selectedOption: 4,
          panels,
          ratioTotal: panelLength > 0 ? (totalCurveLength / panelLength) : 0,
        };

        findBestCombination([wallTypes]);

        return wallTypes;
      }));
  }

  function getTwoDigitNumbers(wallTypes) {
    const results = [];
    wallTypes.forEach((wallType, indexWallType) => {
      wallType.panels.forEach((panel, index) => {
        if (panel.selectedOption === 1) {
          results.push({ id: `${indexWallType + 1}-${index + 1}`, value: handleOption1(panel) });
        } else if (panel.selectedOption === 2) {
          handleOption2(panel).forEach((value, subIndex) => {
            results.push({ id: `${indexWallType + 1}-${index + 1}-${subIndex + 1}`, value });
          });
        } else if (panel.selectedOption === 3) {
          handleOption3(panel).forEach((value, subIndex) => {
            results.push({ id: `${indexWallType + 1}-${index + 1}-${subIndex + 1}`, value });
          });
        }
      });
    });
    if (results.length === 0) {
      return [0];
    }
    if (!results.every(num => num === 0)) {
      return results.filter(num => num !== 0);
    }
    return results;
  }

  function handleOption1(panel) {
    return Math.ceil((panel.ratio % 1) * 100) / 100;
  }

  function handleOption2(panel) {
    const decimalPart = Math.ceil((panel.ratio % 1) * 100) / 100;
    return [decimalPart / 2, decimalPart / 2];
  }

  function handleOption3(panel) {
    const wholeNumber = Math.floor(panel.ratio);
    const divisor = wholeNumber + 1;
    const equalPart = Math.ceil(panel.ratio / divisor * 100) / 100;
    return Array(divisor).fill(equalPart);
  }

  function handleParentChange(wallType, wallTypes) {
    if (wallType.selectedOption === 4) {
      findBestCombination(wallTypes);
    } else {
      wallType.panels.forEach((p) => {
        p.selectedOption = wallType.selectedOption;
      });
    }
  }

  function findBestCombination(wallTypes) {
    const allPanels = wallTypes.flatMap(pt => pt.panels);
    let improved;

    do {
      improved = false;
      for (let i = 0; i < allPanels.length; i++) {
        const panel = allPanels[i];
        const originalOption = panel.selectedOption;
        let bestOption = originalOption;
        let minTrucks = Infinity;

        for (const option of [1, 2, 3]) {
          panel.selectedOption = option;
          const currentPortions = getTwoDigitNumbers(wallTypes);
          const possibleGroups = groupPanels(wallTypes, { value: currentPortions });

          if (possibleGroups.length < minTrucks) {
            minTrucks = possibleGroups.length;
            bestOption = option;
          }
        }

        panel.selectedOption = originalOption;

        if (bestOption !== originalOption) {
          panel.selectedOption = bestOption;
          improved = true;

          const currentPortions = getTwoDigitNumbers(wallTypes);
          const possibleGroups = groupPanels(wallTypes, { value: currentPortions });
          minTrucks = possibleGroups.length;
        }
      }
    } while (improved);
  }

  function groupPanels(panels) {
    const groups = [];

    panels.forEach(panel => {
      let placed = tryPlacePanelInExistingGroups(panel, groups);
      if (!placed) {
        const rotatedPanel = { id: panel.id, length: panel.width, width: panel.length };
        placed = tryPlacePanelInExistingGroups(rotatedPanel, groups);
        if (!placed) {
          createNewGroupWithPanel(panel, groups);
        }
      }
    });

    return groups;
  }

  function tryPlacePanelInExistingGroups(panel, groups) {
    for (const group of groups) {
      if (tryPlacePanel(panel, group.truckBed)) {
        group.panels.push(panel);
        return true;
      }
    }
    return false;
  }

  function createNewGroupWithPanel(panel, groups) {
    const newTruckBed = createTruckBed();

    if (tryPlacePanel(panel, newTruckBed)) {
      groups.push({ panels: [panel], truckBed: newTruckBed });
      return;
    }

    const rotatedPanel = { id: panel.id, length: panel.width, width: panel.length };
    if (tryPlacePanel(rotatedPanel, newTruckBed)) {
      groups.push({ panels: [rotatedPanel], truckBed: newTruckBed });
    }
  }

  function createTruckBed() {
    return {
      length: debouncedTruckLength.value,
      width: debouncedTruckWidth.value,
      usedSpace: []
    };
  }

  function tryPlacePanel(panel, truckBed) {
    const candidates = [{ x: 0, y: 0 }];

    truckBed.usedSpace.forEach(space => {
      candidates.push({ x: space.x + space.length, y: space.y });
      candidates.push({ x: space.x, y: space.y + space.width });
    });

    for (const pos of candidates) {
      if (
        pos.x + panel.length <= truckBed.length &&
        pos.y + panel.width <= truckBed.width &&
        canPlacePanel(panel, pos.x, pos.y, truckBed.usedSpace)
      ) {
        truckBed.usedSpace.push({ x: pos.x, y: pos.y, length: panel.length, width: panel.width });
        return true;
      }
    }
    return false;
  }

  function canPlacePanel(panel, x, y, usedSpace) {
    for (const space of usedSpace) {
      if (
        x < space.x + space.length &&
        x + panel.length > space.x &&
        y < space.y + space.width &&
        y + panel.width > space.y
      ) {
        return false;
      }
    }
    return true;
  }

  function groupByTruck(groups) {
    const truckHeight = debouncedTruckHeight?.value ?? debouncedTruckHeight ?? 0;
    const panelThickness = thickness?.value ?? thickness ?? 0;

    if (truckHeight === 0 || panelThickness === 0) {
      return groups;
    }

    const groupedByTruck = [];
    const maxGroupsPerTruck = Math.floor(truckHeight / panelThickness);
    let truckGroup = [];

    for (const group of groups) {
      if (truckGroup.length < maxGroupsPerTruck) {
        truckGroup.push(group);
      } else {
        groupedByTruck.push(truckGroup);
        truckGroup = [group];
      }
    }

    if (truckGroup.length > 0) {
      groupedByTruck.push(truckGroup);
    }

    return groupedByTruck;
  }

  function getAllFullPanels(wallTypes, maxPanelLength, maxPanelWidth) {
    const panels = [];
    wallTypes.forEach((wallType, indexWallType) => {
      wallType.panels.forEach((panel, index) => {
        if (panel.selectedOption === 3) {
          const roundedRatio = Math.ceil(panel.ratio * 100) / 100;
          const wholeNumber = Math.floor(roundedRatio);
          if (roundedRatio === wholeNumber) {
            for (let i = 0; i < wholeNumber; i++) {
              panels.push({
                id: `${indexWallType + 1}-${index + 1}-${i + 1}`,
                length: maxPanelLength,
                width: maxPanelWidth,
              });
            }
          }
        } else {
          for (let i = 0; i < Math.floor(panel.ratio); i++) {
            panels.push({
              id: `${indexWallType + 1}-${index + 1}-${i + 1}`,
              length: maxPanelLength,
              width: maxPanelWidth,
            });
          }
        }
      });
    });
    return panels;
  }

  function createPanels(wallTypes, pannelPortions, maxPanelLength, maxPanelWidth) {
    const panels = getAllFullPanels(wallTypes, maxPanelLength, maxPanelWidth);

    pannelPortions.value.forEach(portion => {
      panels.push({ id: portion.id, length: portion.value * maxPanelLength, width: maxPanelWidth });
    });

    panels.sort((a, b) => (b.length * b.width) - (a.length * a.width));

    return panels;
  }

  return {
    createWallTypes,
    getTwoDigitNumbers,
    handleParentChange,
    groupPanels,
    groupByTruck,
    createPanels,
  };
}